<template>
    <div class="texts-page">
        <h1>Teksty stron</h1>

        <h3 v-if="!editingId">Aby rozpocząć edycje wciśnij przycisk edytuj przy wybranym tekście</h3>         
        <form v-if="editingId" @submit.prevent="saveText" class="form-container">
            <div class="form-group">
                <div class="first-row">
                    <div class="one-input">
                        <label for="name">Nazwa</label>
                        <input id="name" v-model="form.name" required disabled />
                    </div>

                    <div class="one-input">
                        <label for="page_area">Obszar strony</label>
                        <select id="page_area" v-model="form.page_area" required disabled>
                            <option disabled value="">Wybierz obszar</option>
                            <option value="Stopka">Stopka</option>
                            <option value="O nas">O nas</option>
                            <option value="Regulamin">Regulamin</option>
                            <option value="Polityka prywatności">Polityka prywatności</option>
                            <option value="Kontakt">Kontakt</option>
                        </select>
                    </div>
                </div>

                <label for="content">Treść</label>
                <QuillEditor :key="editorKey" id="content" v-model:content="form.content" contentType="html"
                    theme="snow" toolbar="full" placeholder="Wprowadź treść..." />
            </div>

            <div class="buttons-group-form">
                <button type="submit" class="save-button" :disabled="textsStore.loading">
                    Zapisz zmiany
                </button>
                <button type="button" class="cancel-button" @click="cancelEdit">
                    Anuluj
                </button>
            </div>
        </form>

        <p v-if="textsStore.error" class="error-text">
            Wystąpił błąd: {{ textsStore.error.message || textsStore.error }}
        </p>

        <hr />

        <div class="filter-container">
            <label for="filterArea">Filtruj po obszarze strony:</label>
            <select id="filterArea" v-model="filterArea">
                <option value="">Wszystkie</option>
                <option value="Stopka">Stopka</option>
                <option value="O nas">O nas</option>
                <option value="Regulamin">Regulamin</option>
                <option value="Polityka prywatności">Polityka prywatności</option>
                <option value="Kontakt">Kontakt</option>
            </select>
        </div>

        <div v-if="textsStore.loading" class="loading-text">Ładowanie...</div>
        <div v-else class="texts-container">
            <div class="text-card" v-for="text in filteredTexts" :key="text.id">
                <div class="text-header">
                    <strong>{{ text.name }} ({{ text.page_area }})</strong>
                </div>
                <div class="text-content" v-html="text.content"></div>
                <div class="buttons-group">
                    <button @click="editText(text)">Edytuj</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useTextsStore } from '@/stores/textsStore'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { nextTick } from 'vue'

const textsStore = useTextsStore()

const form = reactive({
    name: '',
    page_area: '',
    content: ''
})
const editingId = ref(null)
const filterArea = ref('')
const editorKey = ref(0)

onMounted(() => {
    textsStore.fetchTexts()
})

const filteredTexts = computed(() => {
    if (!filterArea.value) return textsStore.texts
    return textsStore.texts.filter(text => text.page_area === filterArea.value)
})

const saveText = async () => {
    if (!editingId.value) return
    try {
        await textsStore.updateText(editingId.value, { ...form })
        resetForm()
    } catch (err) {
        console.error(err)
    }
}

const findScrollableParent = (el) => {
    let parent = el.parentElement
    const overflowRegex = /(auto|scroll|overlay)/

    while (parent) {
        const style = window.getComputedStyle(parent)
        const overflowY = style.overflowY
        if (overflowRegex.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
            return parent
        }
        parent = parent.parentElement
    }
    return document.scrollingElement || document.documentElement || document.body
}

const editText = async (text) => {
    editingId.value = text.id
    form.name = text.name
    form.page_area = text.page_area
    form.content = text.content

    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 60))

    const formEl = document.querySelector('.form-container')
    if (!formEl) return

    const scrollable = findScrollableParent(formEl)

    if (
        scrollable === document.scrollingElement ||
        scrollable === document.documentElement ||
        scrollable === document.body
    ) {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
        const parentRect = scrollable.getBoundingClientRect()
        const elRect = formEl.getBoundingClientRect()
        const offset = elRect.top - parentRect.top + scrollable.scrollTop
        scrollable.scrollTo({ top: offset, behavior: 'smooth' })
    }

    setTimeout(() => document.getElementById('name')?.focus(), 350)
}

const cancelEdit = () => resetForm()

const resetForm = () => {
    editingId.value = null
    form.name = ''
    form.page_area = ''
    form.content = ''
    editorKey.value++
}
</script>

<style lang="scss" scoped>
@import '@vueup/vue-quill/dist/vue-quill.snow.css';

.texts-page {
    padding: 20px;

    h1 {
        margin-bottom: 20px;
    }

    .error-text {
        color: red;
        margin-top: 10px;
    }
}

.form-container {
    margin-bottom: 30px;

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 10px;

        label {
            font-weight: 500;
            font-size: 1rem;
        }

        input,
        select {
            font-family: 'Inter', sans-serif;
            padding: 10px;
            border: 2px solid $color-grey;
            border-radius: 8px;
            font-size: 1rem;
            width: 300px;
            transition: border 0.3s ease;

            &:focus {
                border-color: $color-primary;
                outline: none;
            }
        }

        .ql-container.ql-snow,
        .ql-toolbar.ql-snow {
            border-color: $color-grey;
            border-radius: 8px;
        }

        .quill-editor {
            width: 100%;
        }

        .first-row {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
        }

        .one-input {
            display: flex;
            flex-direction: column;
        }
    }

    .buttons-group-form {
        display: flex;
        gap: 10px;
        margin-top: 10px;

        button {
            flex: 1;
            padding: 10px 16px;
            border-radius: 8px;
            border: none;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
        }

        .save-button {
            background-color: $color-primary;
            color: $color-white;
            width: 100%;

            &:hover {
                background-color: $color-primary-dark;
            }
        }

        .cancel-button {
            background-color: $color-grey;
            color: $color-primary;

            &:hover {
                background-color: $color-grey;
            }
        }
    }
}

.filter-container {
    margin-bottom: 15px;

    label {
        font-weight: 500;
        margin-right: 10px;
    }

    select {
        font-family: 'Inter', sans-serif;
        padding: 6px 10px;
        border: 2px solid $color-grey;
        border-radius: 6px;
        font-size: 1rem;
        transition: border 0.3s ease;

        &:focus {
            border-color: $color-primary;
            outline: none;
        }
    }
}

.texts-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.text-card {
    border: 1px solid $color-grey;
    border-radius: 8px;
    padding: 15px;
    background-color: $color-white;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .text-header {
        font-weight: 600;
        margin-bottom: 8px;
    }

    .text-content {
        margin-bottom: 12px;
        white-space: pre-wrap;
    }

    .buttons-group {
        display: flex;
        gap: 10px;

        button {
            flex: 1;
            padding: 8px 12px;
            border-radius: 6px;
            background-color: $color-primary;
            color: $color-white;
            border: none;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s ease;

            &:hover {
                opacity: 0.9;
            }
        }
    }
}

@media (max-width: 1000px) {

    .form-group input,
    .form-group select,
    .form-group textarea {
        width: 100% !important;
    }

    .ql-toolbar.ql-snow+.ql-container.ql-snow {
        width: 100% !important;
    }


    .first-row {
        flex-direction: column;
    }

    .one-input {
        flex: 1 1 100%;
        width: 100%;
    }
}
</style>